(() => {
  if (customElements.get('author-related-articles')) return;

  const normalizeUrl = (value) => {
    try {
      const url = new URL(value, window.location.origin);
      return url.pathname;
    } catch (error) {
      return value;
    }
  };

  class AuthorRelatedArticles extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-ref="list"]');
      this.viewAll = this.querySelector('[data-ref="view-all"]');
      this.load();
    }

    get limit() {
      return Number.parseInt(this.dataset.limit, 10) || 3;
    }

    get tagUrls() {
      if (!this.dataset.blogUrl || !this.dataset.authorHandle) return [];
      const tags = [
        `autoria-${this.dataset.authorHandle}`,
        `author-${this.dataset.authorHandle}`,
        this.dataset.authorHandle,
      ];
      return [...new Set(tags.map((tag) => `${this.dataset.blogUrl}/tagged/${tag}`))];
    }

    get searchUrl() {
      if (!this.dataset.searchUrl || !this.dataset.authorName) return '';
      const query = `"${this.dataset.authorName}"`;
      return `${this.dataset.searchUrl}?type=article&options%5Bprefix%5D=last&q=${encodeURIComponent(query)}`;
    }

    async fetchDocument(url) {
      if (!url) return null;
      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) return null;
      const markup = await response.text();
      return new DOMParser().parseFromString(markup, 'text/html');
    }

    tagCards(documentNode) {
      if (!documentNode) return [];
      const blogPath = normalizeUrl(this.dataset.blogUrl || '');
      return Array.from(documentNode.querySelectorAll("ul[data-ref='article-grid'] > li"))
        .map((item) => ({
          url: normalizeUrl(item.querySelector('a[href]')?.href || ''),
          node: item.cloneNode(true),
        }))
        .filter((item) => blogPath && item.url.startsWith(`${blogPath}/`));
    }

    searchCards(documentNode) {
      if (!documentNode) return [];
      const results = Array.from(documentNode.querySelectorAll('ul.c-search__result-grid > li'));
      const blogPath = normalizeUrl(this.dataset.blogUrl || '');

      return results.flatMap((result) => {
        const sourceLink = result.querySelector(':scope > a.c-search__result-link[href]');
        const sourceArticle = sourceLink?.querySelector('article');
        const sourceTitle = sourceArticle?.querySelector('h2, h3');
        const path = normalizeUrl(sourceLink?.href || '');
        if (!sourceLink || !sourceArticle || !sourceTitle || !blogPath || !path.startsWith(`${blogPath}/`)) return [];

        const item = document.createElement('li');
        const card = document.createElement('article');
        const link = document.createElement('a');
        const title = document.createElement('h3');
        const readMore = document.createElement('a');
        const sourceImage = sourceArticle.querySelector('img');
        card.className = 'c-article-card vl-blog-article-card vl-author-related-card';
        link.className = 'c-article-card__link';
        link.href = path;

        if (sourceImage) {
          const image = sourceImage.cloneNode(true);
          image.className = 'c-article-card__image a-image-background';
          link.appendChild(image);
        }

        title.className = 'c-article-card__title';
        title.textContent = sourceTitle.textContent.trim();
        link.appendChild(title);

        readMore.className = 'vl-article-card__read-more';
        readMore.href = path;
        readMore.textContent = this.dataset.readMore || 'Llegeix-ne més';
        readMore.setAttribute('aria-label', `${readMore.textContent}: ${title.textContent}`);

        card.append(link, readMore);
        item.appendChild(card);
        return [{ url: path, node: item }];
      });
    }

    async load() {
      if (!this.list) return;

      try {
        const tagUrls = this.tagUrls;
        const [tagDocuments, searchDocument] = await Promise.all([
          Promise.all(tagUrls.map((url) => this.fetchDocument(url))),
          this.fetchDocument(this.searchUrl),
        ]);
        const taggedCards = tagDocuments.flatMap((documentNode, index) =>
          this.tagCards(documentNode).map((card) => ({ ...card, sourceUrl: tagUrls[index] }))
        );
        const merged = [...taggedCards, ...this.searchCards(searchDocument)];
        const unique = [];
        const existingItems = Array.from(this.list.children);
        const seen = new Set(
          existingItems
            .map((item) => normalizeUrl(item.dataset.relatedArticleUrl || item.querySelector('a[href]')?.href || ''))
            .filter(Boolean)
        );

        for (const card of merged) {
          if (!card.url || seen.has(card.url)) continue;
          seen.add(card.url);
          unique.push(card);
        }

        if (!unique.length && !existingItems.length) return;

        const availableSlots = Math.max(this.limit - existingItems.length, 0);
        for (const card of unique.slice(0, availableSlots)) {
          this.list.appendChild(card.node);
        }

        if (existingItems.length + unique.length > this.limit && this.viewAll) {
          const link = this.viewAll.querySelector('a');
          if (link) link.href = taggedCards.length > this.limit ? taggedCards[0].sourceUrl : this.searchUrl;
          this.viewAll.hidden = false;
        }

        this.hidden = false;
      } catch (error) {
        console.warn('No s’han pogut carregar els articles relacionats.', error);
      }
    }
  }

  customElements.define('author-related-articles', AuthorRelatedArticles);
})();
